import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import type { CRMType } from './crmApi';
import type {
  CrmField,
  CrmFieldsResponse,
  CrmList,
  CrmListsResponse,
  CrmObjectType,
  CrmSavedImportResponse,
} from '@/lib/types/crm-types';
import type { WorkspaceUploadType } from '@/lib/types/workspace.types';

/**
 * CRM -> Infynd import for the Saved destination. Workspace and Enrichment
 * are NOT here any more: both are live, using the real HubSpot segments
 * (`hubspotApi.getHubspotSegments`) and `workspaceApi.importWorkspaceFromCrm`
 * / `enrichmentApi.importEnrichmentFromCrm` -> `/{workspace,enrichment}/importfromcrm`.
 *
 * Ported from the previous Infynd app's `import_crm_content.tsx`, which is the
 * only place this flow has ever existed end to end. The list and field
 * endpoints are its real contract, kept identical so the service can be lifted
 * across rather than reinterpreted:
 *
 *   GET  /crm/list?objectType&crmType        -> { lists: [...] }
 *   GET  /crm/crm-fields?objectType&crmType  -> { fields: [...] }
 *
 * Neither is reachable from this app yet: `/api/crm` is not mounted in
 * `packages/server/routes.ts`. Until it is, `CRM_IMPORT_BACKEND_READY` keeps
 * these on fixtures.
 */

/** Stable identity for a list across the two id spellings. */
export const crmListId = (list: CrmList) => list.listId ?? list.savedqueryid ?? '';

/**
 * Flip to true once the routes above exist.
 *
 * One flag in one file rather than per-endpoint mocking: the failure this
 * avoids is a half-wired import where the pickers are live but the submit
 * silently no-ops, which looks exactly like success from the UI.
 */
export const CRM_IMPORT_BACKEND_READY = false;

/** The API speaks 'people'; the workspace speaks 'contact'. */
const toApiObjectType = (type: WorkspaceUploadType): CrmObjectType =>
  type === 'contact' ? 'people' : 'company';

const MOCK_LISTS: Record<WorkspaceUploadType, CrmList[]> = {
  contact: [
    { listId: 'list-active-contacts', name: 'Active Contacts', totalRecords: 1250 },
    { listId: 'list-sales-prospects', name: 'Sales Prospects', totalRecords: 3450 },
    { listId: 'list-newsletter', name: 'Newsletter Subscribers', totalRecords: 8920 },
    { listId: 'list-empty', name: 'Webinar Signups (Q3)', totalRecords: 0 },
  ],
  company: [
    { listId: 'list-target-accounts', name: 'Target Accounts', totalRecords: 420 },
    { listId: 'list-open-opportunities', name: 'Open Opportunities', totalRecords: 138 },
    { listId: 'list-churned', name: 'Churned Accounts', totalRecords: 96 },
  ],
};

const MOCK_FIELDS: Record<WorkspaceUploadType, CrmField[]> = {
  contact: [
    { value: 'email', label: 'Email', rawLabel: 'Email', isFixed: true },
    { value: 'firstname', label: 'First Name', rawLabel: 'First Name' },
    { value: 'lastname', label: 'Last Name', rawLabel: 'Last Name' },
    { value: 'jobtitle', label: 'Job Title', rawLabel: 'Job Title' },
    { value: 'company', label: 'Company', rawLabel: 'Company Name' },
    { value: 'hs_linkedin_url', label: 'LinkedIn URL', rawLabel: 'LinkedIn URL' },
    { value: 'phone', label: 'Phone Number', rawLabel: 'Phone Number' },
    { value: 'mobilephone', label: 'Mobile Phone', rawLabel: 'Mobile Phone' },
    { value: 'city', label: 'City', rawLabel: 'City' },
    { value: 'state', label: 'State/Region', rawLabel: 'State/Region' },
    { value: 'country', label: 'Country', rawLabel: 'Country' },
    { value: 'lifecyclestage', label: 'Lifecycle Stage', rawLabel: 'Lifecycle Stage' },
    { value: 'hs_lead_status', label: 'Lead Status', rawLabel: 'Lead Status' },
    { value: 'industry', label: 'Industry', rawLabel: 'Industry' },
    { value: 'website', label: 'Website', rawLabel: 'Website URL' },
  ],
  company: [
    { value: 'domain', label: 'Domain', rawLabel: 'Company Domain Name', isFixed: true },
    { value: 'name', label: 'Name', rawLabel: 'Company Name' },
    { value: 'industry', label: 'Industry', rawLabel: 'Industry' },
    { value: 'linkedin_company_page', label: 'LinkedIn Page', rawLabel: 'LinkedIn Company Page' },
    { value: 'numberofemployees', label: 'Employees', rawLabel: 'Number of Employees' },
    { value: 'annualrevenue', label: 'Annual Revenue', rawLabel: 'Annual Revenue' },
    { value: 'city', label: 'City', rawLabel: 'City' },
    { value: 'state', label: 'State/Region', rawLabel: 'State/Region' },
    { value: 'country', label: 'Country', rawLabel: 'Country' },
    { value: 'phone', label: 'Phone Number', rawLabel: 'Phone Number' },
    { value: 'website', label: 'Website', rawLabel: 'Website URL' },
    { value: 'type', label: 'Type', rawLabel: 'Type' },
    { value: 'founded_year', label: 'Founded Year', rawLabel: 'Year Founded' },
    { value: 'description', label: 'Description', rawLabel: 'About Us' },
  ],
};

/** Stands in for a round trip so loading states are exercised rather than
 *  flashing past in a single frame. */
const mockDelay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface ObjectScopedArgs {
  crmType: CRMType;
  objectType: WorkspaceUploadType;
}

export const crmImportApi = createApi({
  reducerPath: 'crmImportApi',
  baseQuery: axiosBaseQuery('/api'),
  endpoints: (builder) => ({
    getCrmLists: builder.query<CrmList[], ObjectScopedArgs>({
      queryFn: async ({ crmType, objectType }, _api, _extraOptions, baseQuery) => {
        if (!CRM_IMPORT_BACKEND_READY) {
          await mockDelay(600);
          return { data: MOCK_LISTS[objectType] };
        }
        const result = await baseQuery({
          url: '/crm/list',
          params: { objectType: toApiObjectType(objectType), crmType },
        });
        if (result.error) return { error: result.error };
        return { data: (result.data as CrmListsResponse)?.lists ?? [] };
      },
    }),

    getCrmFields: builder.query<CrmField[], ObjectScopedArgs>({
      queryFn: async ({ crmType, objectType }, _api, _extraOptions, baseQuery) => {
        if (!CRM_IMPORT_BACKEND_READY) {
          await mockDelay(700);
          return { data: MOCK_FIELDS[objectType] };
        }
        const result = await baseQuery({
          url: '/crm/crm-fields',
          params: { objectType: toApiObjectType(objectType), crmType },
        });
        if (result.error) return { error: result.error };
        return { data: (result.data as CrmFieldsResponse)?.fields ?? [] };
      },
    }),

    /**
     * Imports straight into the saved contacts/companies store instead of a
     * workspace sheet — what the Saved tabs in Search offer.
     *
     * Each record is written with `source` set to the CRM, which is the field
     * the Saved tab's "Source" column already reads (it only knows `csv` so
     * far). No endpoint serves this yet; the path below is the proposal.
     */
    importCrmToSaved: builder.mutation<
      CrmSavedImportResponse,
      {
        crmType: CRMType;
        objectType: WorkspaceUploadType;
        listId: string;
        start: number;
        end: number;
        fields: CrmField[];
      }
    >({
      queryFn: async (
        { crmType, objectType, listId, start, end, fields },
        _api,
        _extraOptions,
        baseQuery
      ) => {
        if (!CRM_IMPORT_BACKEND_READY) {
          await mockDelay(1100);
          return { data: { imported: Math.max(0, end - start + 1) } };
        }
        const endpoint = objectType === 'contact' ? '/saved-contacts' : '/saved-companies';
        const result = await baseQuery({
          url: `${endpoint}/import-from-crm`,
          method: 'POST',
          body: { crmType, objectType: toApiObjectType(objectType), listId, start, end, fields },
        });
        if (result.error) return { error: result.error };
        return { data: result.data as CrmSavedImportResponse };
      },
    }),
  }),
});

export const {
  useGetCrmListsQuery,
  useGetCrmFieldsQuery,
  useImportCrmToSavedMutation,
} = crmImportApi;

export default crmImportApi;
